'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { isProcessoArbitralEncerrado } from '../utils/arbitralDetailUtils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useToast } from '@/shared/components/providers/ToastProvider';
import { useExportarHistorico } from './useExportarHistorico';
import type { ProcessoArbitralStatus } from '@/features/arbitral';
import { selectAuthUser } from '@/features/auth/store/selectors';
import { downloadCertificado, fetchCertificados } from '../store/certificados/actions';
import { selectCertificados, selectCertificadosLoading } from '../store/certificados/selectors';
import { cancelarProcessoArbitral, clearProcesso, clearStaleTypingUsers, createParteArbitral, criarAndamentoArbitral, downloadDocumentoArbitral, enviarMensagemChat, fetchAndamentosArbitral, fetchDocumentosArbitral, fetchMensagensChat, fetchNaoLidasChat, fetchProcessoArbitral, fetchSolicitacoesArbitral, marcarMensagemLida, patchParteContato, reabrirProcessoArbitral, reenviarNotificacaoBounce, reenviarPendencia, removerDocumentoArbitral, resolverPendencia, selectCancelandoProcesso, selectCreatingParte, selectDocumentosArbitral, selectErrorAndamentos, selectLoadingAndamentos, selectLoadingDocumentos, selectMensagensChat, selectPatchingParteId, selectProcessoArbitral, selectProcessoArbitralError, selectProcessoArbitralLoading, selectReabrindo, selectReenvioParteId, selectRemovingDocumentoId, selectSendingMessage, selectSubmittingAndamento, selectSubmittingPendenciaId, selectTypingUsers } from '../store';
import { arbitralDetalhesService } from '../store/services';
import { useArbitralChatSSE } from './useArbitralChatSSE';
import { ABAS_DETALHE_ARBITRAL } from '../types/arbitralDetailTypes';
import type { ArbitralDetailTab, CreateParteProcessoPayload, TipoParte } from '../types/arbitralDetailTypes';
const PARTES_ATIVAS_TIPOS: TipoParte[] = ['REQUERENTE', 'REQUERIDO', 'ADVOGADO_REQUERENTE', 'ADVOGADO_REQUERIDO', 'REPRESENTANTE_REQUERENTE', 'REPRESENTANTE_REQUERIDO'];
function parseTab(value: string | null): ArbitralDetailTab {
  const conhecida = ABAS_DETALHE_ARBITRAL.find(aba => aba === value);
  return conhecida ?? 'resumo';
}
interface UseArbitralDetailPageInput {
  processoId: string;
}
export function useArbitralDetailPage(t0) {
  const $ = _c(169);
  const {
    processoId
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const processo = useAppSelector(selectProcessoArbitral);
  const documentos = useAppSelector(selectDocumentosArbitral);
  const loadingDocumentos = useAppSelector(selectLoadingDocumentos);
  const isLoading = useAppSelector(selectProcessoArbitralLoading);
  const error = useAppSelector(selectProcessoArbitralError);
  const creatingParte = useAppSelector(selectCreatingParte);
  const patchingParteId = useAppSelector(selectPatchingParteId);
  const reenvioParteId = useAppSelector(selectReenvioParteId);
  const reabrindo = useAppSelector(selectReabrindo);
  const submittingPendenciaId = useAppSelector(selectSubmittingPendenciaId);
  const certificados = useAppSelector(selectCertificados);
  const certificadosLoading = useAppSelector(selectCertificadosLoading);
  const removingDocumentoId = useAppSelector(selectRemovingDocumentoId);
  const submittingAndamento = useAppSelector(selectSubmittingAndamento);
  const loadingAndamentos = useAppSelector(selectLoadingAndamentos);
  const errorAndamentos = useAppSelector(selectErrorAndamentos);
  const mensagensChat = useAppSelector(selectMensagensChat);
  const sendingMessage = useAppSelector(selectSendingMessage);
  const typingUsers = useAppSelector(selectTypingUsers);
  const authUser = useAppSelector(selectAuthUser);
  const currentUserId = authUser?.id ?? "";
  const currentUserName = authUser?.nomeCompleto ?? "Voc\xEA";
  let t1;
  if ($[0] !== searchParams) {
    t1 = parseTab(searchParams.get("tab"));
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialTab = t1;
  const [activeTab, setActiveTab] = useState(initialTab);
  let t2;
  let t3;
  if ($[2] !== dispatch || $[3] !== processoId) {
    t2 = () => {
      dispatch(fetchProcessoArbitral(processoId));
      dispatch(fetchSolicitacoesArbitral(processoId));
      return () => {
        dispatch(clearProcesso());
      };
    };
    t3 = [processoId, dispatch];
    $[2] = dispatch;
    $[3] = processoId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== activeTab || $[7] !== dispatch || $[8] !== processoId) {
    t4 = () => {
      if (activeTab === "certificados") {
        dispatch(fetchCertificados(processoId));
      }
    };
    t5 = [activeTab, processoId, dispatch];
    $[6] = activeTab;
    $[7] = dispatch;
    $[8] = processoId;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[11] !== activeTab || $[12] !== dispatch || $[13] !== processoId || $[14] !== toast) {
    t6 = () => {
      if (activeTab === "documentos") {
        dispatch(fetchDocumentosArbitral(processoId)).then(result => {
          if (fetchDocumentosArbitral.rejected.match(result)) {
            toast.showError("Erro ao carregar documentos", result.payload as string ?? "Tente novamente.");
          }
        });
      }
    };
    t7 = [activeTab, processoId, dispatch, toast];
    $[11] = activeTab;
    $[12] = dispatch;
    $[13] = processoId;
    $[14] = toast;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  if ($[17] !== dispatch || $[18] !== processoId || $[19] !== toast) {
    t8 = () => {
      dispatch(fetchAndamentosArbitral(processoId)).then(result_0 => {
        if (fetchAndamentosArbitral.rejected.match(result_0)) {
          toast.showError("Erro ao carregar andamentos", result_0.payload as string ?? "Tente novamente.");
        }
      });
    };
    $[17] = dispatch;
    $[18] = processoId;
    $[19] = toast;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleRecarregarAndamentos = t8;
  let t10;
  let t9;
  if ($[21] !== activeTab || $[22] !== handleRecarregarAndamentos) {
    t9 = () => {
      if (activeTab === "andamentos") {
        handleRecarregarAndamentos();
      }
    };
    t10 = [activeTab, handleRecarregarAndamentos];
    $[21] = activeTab;
    $[22] = handleRecarregarAndamentos;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[25] !== activeTab || $[26] !== dispatch || $[27] !== processoId || $[28] !== toast) {
    t11 = () => {
      if (activeTab === "chat") {
        dispatch(fetchMensagensChat(processoId)).then(result_1 => {
          if (fetchMensagensChat.rejected.match(result_1)) {
            toast.showError("Erro ao carregar mensagens", result_1.payload as string ?? "Tente novamente.");
          }
        });
      }
    };
    t12 = [activeTab, processoId, dispatch, toast];
    $[25] = activeTab;
    $[26] = dispatch;
    $[27] = processoId;
    $[28] = toast;
    $[29] = t11;
    $[30] = t12;
  } else {
    t11 = $[29];
    t12 = $[30];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[31] !== activeTab || $[32] !== dispatch || $[33] !== mensagensChat || $[34] !== processoId) {
    t13 = () => {
      if (activeTab !== "chat") {
        return;
      }
      const naoLidas = mensagensChat.filter(m => !m.lida && m.autorTipo !== "SISTEMA" && !markedInSessionRef.current.has(m.id));
      if (naoLidas.length === 0) {
        return;
      }
      const timer = setTimeout(() => {
        (async () => {
          await Promise.allSettled(naoLidas.map(m_0 => {
            markedInSessionRef.current.add(m_0.id);
            return dispatch(marcarMensagemLida({
              processoId,
              msgId: m_0.id
            }));
          }));
          dispatch(fetchNaoLidasChat(processoId));
        })();
      }, 1000);
      return () => clearTimeout(timer);
    };
    t14 = [activeTab, mensagensChat, processoId, dispatch];
    $[31] = activeTab;
    $[32] = dispatch;
    $[33] = mensagensChat;
    $[34] = processoId;
    $[35] = t13;
    $[36] = t14;
  } else {
    t13 = $[35];
    t14 = $[36];
  }
  useEffect(t13, t14);
  const t15 = activeTab === "chat";
  let t16;
  if ($[37] !== processoId || $[38] !== t15) {
    t16 = {
      processoId,
      enabled: t15
    };
    $[37] = processoId;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  useArbitralChatSSE(t16);
  const [chatMessage, setChatMessage] = useState("");
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = new Set();
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const markedInSessionRef = useRef(t17);
  const typingStopTimerRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const isTypingActiveRef = useRef(false);
  let t18;
  let t19;
  if ($[41] !== activeTab || $[42] !== dispatch) {
    t18 = () => {
      if (activeTab !== "chat") {
        return;
      }
      const interval = setInterval(() => {
        dispatch(clearStaleTypingUsers());
      }, 1000);
      return () => clearInterval(interval);
    };
    t19 = [activeTab, dispatch];
    $[41] = activeTab;
    $[42] = dispatch;
    $[43] = t18;
    $[44] = t19;
  } else {
    t18 = $[43];
    t19 = $[44];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[45] !== activeTab) {
    t20 = () => {
      if (activeTab !== "chat") {
        if (typingStopTimerRef.current) {
          clearTimeout(typingStopTimerRef.current);
        }
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
        }
        isTypingActiveRef.current = false;
      }
    };
    t21 = [activeTab];
    $[45] = activeTab;
    $[46] = t20;
    $[47] = t21;
  } else {
    t20 = $[46];
    t21 = $[47];
  }
  useEffect(t20, t21);
  let t22;
  if ($[48] !== activeTab || $[49] !== processoId) {
    t22 = value => {
      setChatMessage(value);
      if (activeTab !== "chat") {
        return;
      }
      if (!isTypingActiveRef.current) {
        isTypingActiveRef.current = true;
        arbitralDetalhesService.enviarTypingEvent(processoId);
        typingIntervalRef.current = setInterval(() => {
          arbitralDetalhesService.enviarTypingEvent(processoId);
        }, 2000);
      }
      if (typingStopTimerRef.current) {
        clearTimeout(typingStopTimerRef.current);
      }
      typingStopTimerRef.current = setTimeout(() => {
        isTypingActiveRef.current = false;
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
      }, 2000);
    };
    $[48] = activeTab;
    $[49] = processoId;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const handleChatMessageChange = t22;
  const isCanceling = useAppSelector(selectCancelandoProcesso);
  processo?.status;
  const t23 = processo?.status;
  let t24;
  if ($[51] !== t23) {
    t24 = isProcessoArbitralEncerrado(t23);
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  const isClosed = t24;
  let t25;
  if ($[53] !== processo?.partes) {
    t25 = () => (processo?.partes ?? []).some(_temp);
    $[53] = processo?.partes;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  processo?.partes;
  let t26;
  if ($[55] !== t25) {
    t26 = t25();
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  const isParteAtiva = t26;
  let t27;
  if ($[57] !== processoId || $[58] !== router || $[59] !== searchParams) {
    t27 = tab => {
      setActiveTab(tab as ArbitralDetailTab);
      const params = new URLSearchParams(searchParams.toString());
      params.set("tab", tab);
      router.replace(`/arbitral/${processoId}?${params.toString()}`, {
        scroll: false
      });
    };
    $[57] = processoId;
    $[58] = router;
    $[59] = searchParams;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const handleTabChange = t27;
  let t28;
  if ($[61] !== router) {
    t28 = () => {
      router.push("/arbitral");
    };
    $[61] = router;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  const handleBackToList = t28;
  let t29;
  if ($[63] !== chatMessage || $[64] !== dispatch || $[65] !== processoId || $[66] !== toast) {
    t29 = async arquivos => {
      if (!chatMessage.trim() && arquivos.length === 0) {
        return;
      }
      const result_2 = await dispatch(enviarMensagemChat({
        processoId,
        texto: chatMessage,
        arquivos
      }));
      if (enviarMensagemChat.fulfilled.match(result_2)) {
        setChatMessage("");
      } else {
        toast.showError("Erro ao enviar mensagem", result_2.payload as string ?? "Tente novamente.");
      }
    };
    $[63] = chatMessage;
    $[64] = dispatch;
    $[65] = processoId;
    $[66] = toast;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  const handleSendMessage = t29;
  const {
    exportar: handleExportHistory,
    exportando: exportandoHistorico
  } = useExportarHistorico(processoId, processo?.codigo ?? processoId);
  let t30;
  if ($[68] !== dispatch || $[69] !== processoId || $[70] !== toast) {
    t30 = async (_motivo, _comentario) => {
      const result_3 = await dispatch(cancelarProcessoArbitral({
        processoId
      }));
      if (cancelarProcessoArbitral.fulfilled.match(result_3)) {
        if (result_3.payload.resultado.modo === "DIRETO") {
          toast.showSuccess("Processo cancelado", "O processo foi cancelado.");
        } else {
          toast.showSuccess("Solicita\xE7\xE3o enviada", "Seu pedido de cancelamento foi enviado para a secretaria.");
        }
      } else {
        toast.showError("Erro ao cancelar", result_3.payload as string ?? "Tente novamente.");
      }
    };
    $[68] = dispatch;
    $[69] = processoId;
    $[70] = toast;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  const handleCancelProcess = t30;
  let t31;
  if ($[72] !== dispatch || $[73] !== processoId || $[74] !== toast) {
    t31 = async (novoStatus, motivo) => {
      const result_4 = await dispatch(reabrirProcessoArbitral({
        processoId,
        novoStatus,
        motivo
      }));
      if (reabrirProcessoArbitral.fulfilled.match(result_4)) {
        toast.showSuccess("Processo reaberto", "O processo voltou a tramitar.");
        await dispatch(fetchProcessoArbitral(processoId));
      } else {
        toast.showError("Erro ao reabrir", result_4.payload as string ?? "Tente novamente.");
        throw new Error("reabrir failed");
      }
    };
    $[72] = dispatch;
    $[73] = processoId;
    $[74] = toast;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  const handleReabrirProcesso = t31;
  let t32;
  if ($[76] !== dispatch || $[77] !== processoId || $[78] !== toast) {
    t32 = async (parteId, data) => {
      const result_5 = await dispatch(patchParteContato({
        processoId,
        parteId,
        data
      }));
      if (patchParteContato.fulfilled.match(result_5)) {
        toast.showSuccess("Contato atualizado", "Os dados de contato foram salvos com sucesso.");
      } else {
        toast.showError("Erro ao atualizar contato", result_5.payload as string ?? "Tente novamente.");
        throw new Error("patch failed");
      }
    };
    $[76] = dispatch;
    $[77] = processoId;
    $[78] = toast;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  const handlePatchParte = t32;
  let t33;
  if ($[80] !== dispatch || $[81] !== processoId || $[82] !== toast) {
    t33 = async data_0 => {
      const result_6 = await dispatch(createParteArbitral({
        processoId,
        data: data_0
      }));
      if (createParteArbitral.fulfilled.match(result_6)) {
        toast.showSuccess("Parte adicionada", "A nova parte foi inclu\xEDda no processo com sucesso.");
        return;
      }
      toast.showError("Erro ao adicionar parte", result_6.payload as string ?? "Tente novamente.");
      throw new Error("create parte failed");
    };
    $[80] = dispatch;
    $[81] = processoId;
    $[82] = toast;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  const handleAddParte = t33;
  let t34;
  if ($[84] !== dispatch || $[85] !== processoId || $[86] !== toast) {
    t34 = async parteId_0 => {
      const result_7 = await dispatch(reenviarNotificacaoBounce({
        processoId,
        parteId: parteId_0
      }));
      if (reenviarNotificacaoBounce.fulfilled.match(result_7)) {
        toast.showSuccess("Notifica\xE7\xE3o reenviada", "A notifica\xE7\xE3o foi enviada com sucesso.");
      } else {
        toast.showError("Erro ao reenviar notifica\xE7\xE3o", result_7.payload as string ?? "Tente novamente.");
      }
    };
    $[84] = dispatch;
    $[85] = processoId;
    $[86] = toast;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  const handleReenviarNotificacao = t34;
  let t35;
  if ($[88] !== dispatch || $[89] !== processoId || $[90] !== toast) {
    t35 = async (pendenciaId, resposta, arquivos_0) => {
      const result_8 = await dispatch(resolverPendencia({
        processoId,
        pendenciaId,
        resposta,
        arquivos: arquivos_0
      }));
      if (resolverPendencia.fulfilled.match(result_8)) {
        toast.showSuccess("Resposta enviada", "Sua resposta foi enviada para valida\xE7\xE3o da Secretaria.");
      } else {
        toast.showError("Erro ao resolver pend\xEAncia", result_8.payload as string ?? "Tente novamente.");
        throw new Error("resolver failed");
      }
    };
    $[88] = dispatch;
    $[89] = processoId;
    $[90] = toast;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  const handleResolverPendencia = t35;
  let t36;
  if ($[92] !== dispatch || $[93] !== processoId || $[94] !== toast) {
    t36 = async (pendenciaId_0, resposta_0, arquivos_1) => {
      const result_9 = await dispatch(reenviarPendencia({
        processoId,
        pendenciaId: pendenciaId_0,
        resposta: resposta_0,
        arquivos: arquivos_1
      }));
      if (reenviarPendencia.fulfilled.match(result_9)) {
        toast.showSuccess("Pend\xEAncia reenviada", "Sua resposta foi enviada para valida\xE7\xE3o.");
      } else {
        toast.showError("Erro ao reenviar pend\xEAncia", result_9.payload as string ?? "Tente novamente.");
        throw new Error("reenviar failed");
      }
    };
    $[92] = dispatch;
    $[93] = processoId;
    $[94] = toast;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  const handleReenviarPendencia = t36;
  let t37;
  if ($[96] !== dispatch || $[97] !== processoId || $[98] !== toast) {
    t37 = async docId => {
      const result_10 = await dispatch(removerDocumentoArbitral({
        processoId,
        docId
      }));
      if (removerDocumentoArbitral.fulfilled.match(result_10)) {
        toast.showSuccess("Documento removido", "O documento foi exclu\xEDdo com sucesso.");
      } else {
        toast.showError("Erro ao remover documento", result_10.payload as string ?? "Tente novamente.");
      }
    };
    $[96] = dispatch;
    $[97] = processoId;
    $[98] = toast;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  const handleRemoverDocumento = t37;
  let t38;
  if ($[100] !== dispatch || $[101] !== processoId || $[102] !== toast) {
    t38 = async (docId_0, nomeOriginal) => {
      const result_11 = await dispatch(downloadDocumentoArbitral({
        processoId,
        docId: docId_0,
        nomeOriginal
      }));
      if (downloadDocumentoArbitral.rejected.match(result_11)) {
        toast.showError("Erro no download", "N\xE3o foi poss\xEDvel gerar o link. Tente novamente.");
      }
    };
    $[100] = dispatch;
    $[101] = processoId;
    $[102] = toast;
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  const handleDownloadDocumento = t38;
  let t39;
  if ($[104] !== processoId || $[105] !== router) {
    t39 = () => {
      router.push(`/arbitral/${processoId}/certificado-acessos`);
    };
    $[104] = processoId;
    $[105] = router;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  const handleVerCertificadoAcessos = t39;
  let t40;
  if ($[107] !== processoId || $[108] !== router) {
    t40 = () => {
      router.push(`/arbitral/${processoId}/certificado-acessos-sentenca`);
    };
    $[107] = processoId;
    $[108] = router;
    $[109] = t40;
  } else {
    t40 = $[109];
  }
  const handleVerCertificadoAcessosSentenca = t40;
  let t41;
  if ($[110] !== dispatch || $[111] !== processoId || $[112] !== toast) {
    t41 = async certificadoId => {
      const result_12 = await dispatch(downloadCertificado({
        processoId,
        certificadoId
      }));
      if (downloadCertificado.rejected.match(result_12)) {
        toast.showError("Erro no download", "N\xE3o foi poss\xEDvel gerar o link. Tente novamente.");
        return;
      }
      window.open(result_12.payload, "_blank", "noopener,noreferrer");
    };
    $[110] = dispatch;
    $[111] = processoId;
    $[112] = toast;
    $[113] = t41;
  } else {
    t41 = $[113];
  }
  const handleBaixarCertificado = t41;
  let t42;
  if ($[114] !== processoId || $[115] !== toast) {
    t42 = async docId_1 => {
      try {
        return await arbitralDetalhesService.previewDocumento(processoId, docId_1);
      } catch {
        toast.showError("Erro ao carregar preview", "N\xE3o foi poss\xEDvel carregar o documento. Tente novamente.");
        throw new Error("preview failed");
      }
    };
    $[114] = processoId;
    $[115] = toast;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  const handleGetPreviewUrl = t42;
  let t43;
  if ($[117] !== dispatch || $[118] !== processoId || $[119] !== toast) {
    t43 = async dados => {
      const result_13 = await dispatch(criarAndamentoArbitral({
        processoId,
        descricao: dados.texto,
        arquivos: dados.arquivos
      }));
      if (criarAndamentoArbitral.fulfilled.match(result_13)) {
        toast.showSuccess("Andamento registrado", "O andamento foi salvo com sucesso.");
      } else {
        toast.showError("Erro ao registrar andamento", result_13.payload as string ?? "Tente novamente.");
        throw new Error("criar andamento failed");
      }
    };
    $[117] = dispatch;
    $[118] = processoId;
    $[119] = toast;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  const handleCriarAndamento = t43;
  let t44;
  if ($[121] !== activeTab || $[122] !== certificados || $[123] !== certificadosLoading || $[124] !== chatMessage || $[125] !== creatingParte || $[126] !== currentUserId || $[127] !== currentUserName || $[128] !== documentos || $[129] !== error || $[130] !== errorAndamentos || $[131] !== exportandoHistorico || $[132] !== handleAddParte || $[133] !== handleBackToList || $[134] !== handleBaixarCertificado || $[135] !== handleCancelProcess || $[136] !== handleChatMessageChange || $[137] !== handleCriarAndamento || $[138] !== handleDownloadDocumento || $[139] !== handleExportHistory || $[140] !== handleGetPreviewUrl || $[141] !== handlePatchParte || $[142] !== handleReabrirProcesso || $[143] !== handleRecarregarAndamentos || $[144] !== handleReenviarNotificacao || $[145] !== handleReenviarPendencia || $[146] !== handleRemoverDocumento || $[147] !== handleResolverPendencia || $[148] !== handleSendMessage || $[149] !== handleTabChange || $[150] !== handleVerCertificadoAcessos || $[151] !== handleVerCertificadoAcessosSentenca || $[152] !== isCanceling || $[153] !== isClosed || $[154] !== isLoading || $[155] !== isParteAtiva || $[156] !== loadingAndamentos || $[157] !== loadingDocumentos || $[158] !== mensagensChat || $[159] !== patchingParteId || $[160] !== processo || $[161] !== reabrindo || $[162] !== reenvioParteId || $[163] !== removingDocumentoId || $[164] !== sendingMessage || $[165] !== submittingAndamento || $[166] !== submittingPendenciaId || $[167] !== typingUsers) {
    t44 = {
      activeTab,
      setActiveTab: handleTabChange,
      isLoading,
      error,
      processo,
      documentos,
      loadingDocumentos,
      mensagensChat,
      isClosed,
      chatMessage,
      setChatMessage: handleChatMessageChange,
      isSendingMessage: sendingMessage,
      typingUsers,
      handleSendMessage,
      handleBackToList,
      handleExportHistory,
      exportandoHistorico,
      handleCancelProcess,
      isCanceling,
      handleReabrirProcesso,
      reabrindo,
      creatingParte,
      handleAddParte,
      handlePatchParte,
      handleReenviarNotificacao,
      patchingParteId,
      reenvioParteId,
      certificados,
      certificadosLoading,
      handleVerCertificadoAcessos,
      handleVerCertificadoAcessosSentenca,
      handleBaixarCertificado,
      submittingPendenciaId,
      isParteAtiva,
      handleResolverPendencia,
      handleReenviarPendencia,
      removingDocumentoId,
      currentUserId,
      currentUserName,
      handleRemoverDocumento,
      handleDownloadDocumento,
      handleGetPreviewUrl,
      loadingAndamentos,
      errorAndamentos,
      handleRecarregarAndamentos,
      submittingAndamento,
      handleCriarAndamento
    };
    $[121] = activeTab;
    $[122] = certificados;
    $[123] = certificadosLoading;
    $[124] = chatMessage;
    $[125] = creatingParte;
    $[126] = currentUserId;
    $[127] = currentUserName;
    $[128] = documentos;
    $[129] = error;
    $[130] = errorAndamentos;
    $[131] = exportandoHistorico;
    $[132] = handleAddParte;
    $[133] = handleBackToList;
    $[134] = handleBaixarCertificado;
    $[135] = handleCancelProcess;
    $[136] = handleChatMessageChange;
    $[137] = handleCriarAndamento;
    $[138] = handleDownloadDocumento;
    $[139] = handleExportHistory;
    $[140] = handleGetPreviewUrl;
    $[141] = handlePatchParte;
    $[142] = handleReabrirProcesso;
    $[143] = handleRecarregarAndamentos;
    $[144] = handleReenviarNotificacao;
    $[145] = handleReenviarPendencia;
    $[146] = handleRemoverDocumento;
    $[147] = handleResolverPendencia;
    $[148] = handleSendMessage;
    $[149] = handleTabChange;
    $[150] = handleVerCertificadoAcessos;
    $[151] = handleVerCertificadoAcessosSentenca;
    $[152] = isCanceling;
    $[153] = isClosed;
    $[154] = isLoading;
    $[155] = isParteAtiva;
    $[156] = loadingAndamentos;
    $[157] = loadingDocumentos;
    $[158] = mensagensChat;
    $[159] = patchingParteId;
    $[160] = processo;
    $[161] = reabrindo;
    $[162] = reenvioParteId;
    $[163] = removingDocumentoId;
    $[164] = sendingMessage;
    $[165] = submittingAndamento;
    $[166] = submittingPendenciaId;
    $[167] = typingUsers;
    $[168] = t44;
  } else {
    t44 = $[168];
  }
  return t44;
}
function _temp(p) {
  return p.isCurrentUser && PARTES_ATIVAS_TIPOS.includes(p.tipo);
}